<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  TransitionRoot,
  TransitionChild,
} from '@headlessui/vue'

withDefaults(
  defineProps<{
    open: boolean
    title?: string
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '5xl'
    showClose?: boolean
    /** false — контент остаётся в DOM после закрытия (нужно для внешних виджетов). */
    unmount?: boolean
  }>(),
  {
    maxWidth: 'lg',
    showClose: true,
    unmount: true,
  },
)

const emit = defineEmits<{
  close: []
}>()

const widthClass: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '5xl': 'max-w-5xl',
}
</script>

<template>
  <TransitionRoot appear :show="open" :unmount="unmount" as="template">
    <Dialog class="relative z-[999]" @close="emit('close')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/60" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 z-[999] overflow-y-auto">
        <div class="flex min-h-full items-start justify-center px-4 py-8">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              :class="[
                'panel my-0 w-full overflow-hidden rounded-lg border-0 p-0 text-black',
                widthClass[maxWidth],
              ]"
            >
              <div
                v-if="$slots.header || title || showClose"
                class="modal-header"
              >
                <slot name="header">
                  <div class="text-lg font-bold">{{ title }}</div>
                </slot>
                <button
                  v-if="showClose"
                  type="button"
                  class="modal-close"
                  aria-label="Закрыть"
                  @click="emit('close')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-5 w-5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div :class="$slots.footer ? 'px-5 py-5' : 'p-5'">
                <slot />
              </div>

              <div v-if="$slots.footer" class="modal-footer">
                <slot name="footer" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
